import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { buildYears, buildDays, MONTHS } from '~/utils/dueDate';
import { isPhoneValid } from '~/utils/util';
import DialogPrompt from '~/components/common/DialogPrompt/DialogPrompt';

import css from './LeadForm.less';

const DownIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none">
      <g opacity="0.75">
        <circle cx="15" cy="15" r="15" fill="#EFEDEC" />
        <path d="M9 12.25L15 18.25L21 12.25" stroke="#918D89" strokeWidth="2" strokeLinejoin="round" />
      </g>
    </svg>
  );
};

const iconCalendar = require('~/static/index/icon_calendar.png');
const iconPhone = require('~/static/index/icon_phone.png');

const LeadForm = ({ onSubmit, variant = 'overlay' }) => {
  const isInlineVariant = variant === 'inline';
  const years = useMemo(() => buildYears(4), []);
  const [year, setYear] = useState(years[0]);
  const [month, setMonth] = useState(MONTHS[new Date().getMonth()]);
  const [day, setDay] = useState('');
  const [phone, setPhone] = useState('');
  const [promptMessage, setPromptMessage] = useState(null);
  const yearSelectRef = useRef(null);
  const monthSelectRef = useRef(null);
  const daySelectRef = useRef(null);

  const [uniqueId, setUniqueId] = useState('default');

  useEffect(() => {
    setUniqueId(
      Math.random()
        .toString(36)
        .slice(2, 8),
    );
  }, []);

  const controlIds = useMemo(() => {
    return {
      year: `lead-year-${uniqueId}`,
      month: `lead-month-${uniqueId}`,
      day: `lead-day-${uniqueId}`,
      phone: `lead-phone-${uniqueId}`,
    };
  }, [uniqueId]);

  const daysInMonth = useMemo(() => {
    if (!month) {
      return buildDays(31);
    }
    const date = new Date(year, month, 0);
    return buildDays(date.getDate());
  }, [year, month]);

  const handleSubmit = event => {
    event.preventDefault();
    if (onSubmit && year && month && day && phone) {
      if (!isPhoneValid(phone)) {
        setPromptMessage('请输入正确的手机号');
        return;
      }
      onSubmit({ year, month, day, phone });
    }
  };

  const handlePromptConfirm = () => {
    setPromptMessage(null);
  };

  return (
    <section
      className={classNames(css.section, {
        [css.inline]: isInlineVariant,
      })}
    >
      <form className={css.form} onSubmit={handleSubmit}>
        {!isInlineVariant ? <h3>输入您的更多信息，查看有档期的月嫂</h3> : null}
        <div className={css.fields}>
          <fieldset className={css.dateField}>
            <legend className={css.visuallyHidden}>预产日期</legend>
            <span className={css.fieldIcon} aria-hidden>
              <img src={iconCalendar} alt="" aria-hidden="true" />
            </span>
            <span className={css.fieldDivider} aria-hidden />
            <div className={css.dateSegments}>
              <div className={css.dateSegment}>
                <span className={css.valueDisplay}>{year}</span>
                <select
                  className={css.overlaySelect}
                  id={controlIds.year}
                  aria-label="预产年份"
                  value={year}
                  onChange={event => setYear(Number(event.target.value))}
                  ref={yearSelectRef}
                >
                  {years.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <span className={css.segmentSuffix}>年</span>
                <div className={css.segmentArrow} aria-hidden>
                  <DownIcon />
                </div>
              </div>
              <div className={css.dateSegment}>
                <span className={css.valueDisplay}>{String(month).padStart(2, '0')}</span>
                <select
                  className={css.overlaySelect}
                  id={controlIds.month}
                  aria-label="预产月份"
                  value={month}
                  onChange={event => setMonth(Number(event.target.value))}
                  ref={monthSelectRef}
                >
                  {MONTHS.map(option => (
                    <option key={option} value={option}>
                      {String(option).padStart(2, '0')}
                    </option>
                  ))}
                </select>
                <span className={css.segmentSuffix}>月</span>
                <div className={css.segmentArrow} aria-hidden>
                  <DownIcon />
                </div>
              </div>
              <div className={classNames(css.dateSegment, { [css.segmentPlaceholder]: !day })}>
                <span className={css.valueDisplay}>{day ? String(day).padStart(2, '0') : '--'}</span>
                <select
                  className={css.overlaySelect}
                  id={controlIds.day}
                  aria-label="预产日期"
                  value={day}
                  onChange={event => setDay(event.target.value)}
                  ref={daySelectRef}
                >
                  <option value="">--</option>
                  {daysInMonth.map(option => (
                    <option key={option} value={option}>
                      {String(option).padStart(2, '0')}
                    </option>
                  ))}
                </select>
                <span className={css.segmentSuffix}>日</span>
                <div className={css.segmentArrow} aria-hidden>
                  <DownIcon />
                </div>
              </div>
            </div>
          </fieldset>
          <label className={css.phoneField} htmlFor={controlIds.phone}>
            <span className={css.visuallyHidden}>联系方式</span>
            <span className={css.fieldIcon} aria-hidden>
              <img src={iconPhone} alt="" aria-hidden="true" />
            </span>
            <span className={css.fieldDivider} aria-hidden />
            <input
              id={controlIds.phone}
              value={phone}
              onChange={event => setPhone(event.target.value)}
              type="tel"
              placeholder="请输入您的联系方式"
              maxLength={11}
            />
          </label>
          <button className={css.submit} type="submit">
            提交
          </button>
        </div>
      </form>
      {promptMessage ? <DialogPrompt message={promptMessage} onConfirm={handlePromptConfirm} /> : null}
    </section>
  );
};

export default LeadForm;
